import React, { useEffect, useState } from "react";
import { BarChart3, X } from "lucide-react";

import { useStatistics, useTaskStats } from "@/store";
import { fetchUserStatistics, fetchRanking } from "@/apis";

interface StatisticsProps {
  onClose: () => void;
}

type ApiTaskStats = {
  totalTasks: number;
  completedTasks: number;
  totalPomodorosEstimated: number;
  totalPomodorosCompleted: number;
  completionRate: number;
};

type ApiStatistics = {
  streak: number;
  days_access: number;
  total_hours: number;
  task_stats: ApiTaskStats;
};

type RankingEntry = {
  name: string;
  streak: number;
  total_hours: number;
};

const Statistics: React.FC<StatisticsProps> = ({ onClose }) => {
  const taskStats = useTaskStats();
  const localStats = useStatistics();
  const [showDataManagement, setShowDataManagement] = useState<boolean>(false);
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiStats, setApiStats] = useState<ApiStatistics | null>(null);
  const [activeTab, setActiveTab] = useState<"personal" | "ranking">(
    "personal"
  );
  const [ranking, setRanking] = useState<RankingEntry[]>([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [rankingError, setRankingError] = useState<string | null>(null);

  const fetchStatsAndRanking = async () => {
    setLoading(true);
    setError(null);
    setRankingLoading(true);
    setRankingError(null);

    try {
      const [statsRes, rankingRes] = await Promise.all([
        fetchUserStatistics(),
        fetchRanking(),
      ]);

      if (statsRes?.statistics) {
        setApiStats(statsRes.statistics as ApiStatistics);
      }

      if (rankingRes?.ranking) {
        setRanking(rankingRes.ranking as RankingEntry[]);
      }
    } catch (err: any) {
      const message = err.message || "Unable to load data";
      setError(message);
      setRankingError(message);
    } finally {
      setLoading(false);
      setRankingLoading(false);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const token = localStorage.getItem("pomozid_token");
    const isLoggedIn = !!token;
    setRequiresLogin(!isLoggedIn);
    if (!isLoggedIn) {
      return;
    }

    fetchStatsAndRanking();
  }, []);

  const displayStats = apiStats
    ? {
        streak: apiStats.streak,
        daysAccessed: apiStats.days_access,
        totalHours: apiStats.total_hours,
      }
    : {
        streak: localStats.streak,
        daysAccessed: localStats.daysAccessed,
        totalHours: localStats.totalHours,
      };

  const displayTaskStats = apiStats?.task_stats ?? taskStats;

  if (requiresLogin) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <BarChart3 className="text-red-500" size={24} />
              <h2 className="text-2xl font-bold text-gray-800">Statistics</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg">
            You need to log in to view your statistics.
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <BarChart3 className="text-red-500" size={24} />
            <h2 className="text-2xl font-bold text-gray-800">Statistics</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex mb-4 border-b border-gray-200">
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === "personal"
                ? "border-red-500 text-red-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("personal")}
          >
            Personal Stats
          </button>
          <button
            className={`ml-4 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === "ranking"
                ? "border-red-500 text-red-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("ranking")}
          >
            Ranking
          </button>
        </div>

        <div className="space-y-6">
          {/* Global loading / error */}
          {loading && (
            <div className="space-y-6">
              {/* Loading skeleton for personal stats */}
              <div className="animate-pulse">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-3"></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="h-8 bg-gray-200 rounded w-12 mx-auto mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div>
                    </div>
                    <div className="text-center">
                      <div className="h-8 bg-gray-200 rounded w-12 mx-auto mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div>
                    </div>
                    <div className="text-center">
                      <div className="h-8 bg-gray-200 rounded w-12 mx-auto mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-3"></div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <div className="h-8 bg-gray-200 rounded w-12 mx-auto mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div>
                    </div>
                    <div className="text-center">
                      <div className="h-8 bg-gray-200 rounded w-12 mx-auto mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-red-600 font-medium">
                    Error loading statistics
                  </div>
                  <div className="text-red-600 text-sm mt-1">{error}</div>
                </div>
                <button
                  onClick={fetchStatsAndRanking}
                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {activeTab === "personal" && !loading && (
            <>
              {/* Overall Statistics */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Overall Statistics
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {displayStats.streak}
                    </div>
                    <div className="text-sm text-gray-600">Day Streak</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {displayStats.daysAccessed}
                    </div>
                    <div className="text-sm text-gray-600">Days Active</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {displayStats.totalHours}
                    </div>
                    <div className="text-sm text-gray-600">Total Hours</div>
                  </div>
                </div>
              </div>

              {/* Today's Stats */}
              <div className="bg-red-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Today&apos;s Progress
                </h3>
              </div>

              {/* Overall Task Stats */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Task Statistics
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {displayTaskStats.totalTasks}
                    </div>
                    <div className="text-sm text-gray-600">Total Tasks</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {displayTaskStats.completedTasks}
                    </div>
                    <div className="text-sm text-gray-600">Completed</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {displayTaskStats.totalPomodorosEstimated}
                    </div>
                    <div className="text-sm text-gray-600">Est. Pomodoros</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {displayTaskStats.totalPomodorosCompleted}
                    </div>
                    <div className="text-sm text-gray-600">Done Pomodoros</div>
                  </div>
                </div>

                {/* Completion Rate */}
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">
                      Task Completion Rate
                    </span>
                    <span className="text-sm font-semibold text-gray-800">
                      {displayTaskStats.completionRate.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${displayTaskStats.completionRate}%` }}
                    ></div>
                  </div>
                </div>

                {/* Pomodoro Accuracy */}
                {displayTaskStats.totalPomodorosEstimated > 0 && (
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">
                        Pomodoro Accuracy
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {(
                          (displayTaskStats.totalPomodorosCompleted /
                            displayTaskStats.totalPomodorosEstimated) *
                          100
                        ).toFixed(1)}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(
                            (displayTaskStats.totalPomodorosCompleted /
                              displayTaskStats.totalPomodorosEstimated) *
                              100,
                            100
                          )}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Data Management */}
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Data Management
                  </h3>
                  <button
                    onClick={() => setShowDataManagement(!showDataManagement)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showDataManagement ? "Hide" : "Show"} Options
                  </button>
                </div>

                {showDataManagement && <div className="space-y-3"></div>}
              </div>
            </>
          )}

          {activeTab === "ranking" && !loading && (
            <div className="space-y-4">
              {rankingLoading && (
                <div className="animate-pulse">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                    </div>
                    <div className="p-4 space-y-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="flex items-center space-x-4">
                          <div className="h-4 bg-gray-200 rounded w-8"></div>
                          <div className="h-4 bg-gray-200 rounded w-24"></div>
                          <div className="h-4 bg-gray-200 rounded w-12"></div>
                          <div className="h-4 bg-gray-200 rounded w-16"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {rankingError && !rankingLoading && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-red-600 font-medium">
                        Error loading ranking
                      </div>
                      <div className="text-red-600 text-sm mt-1">
                        {rankingError}
                      </div>
                    </div>
                    <button
                      onClick={fetchStatsAndRanking}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              )}

              {!rankingLoading && !rankingError && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Leaderboard
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            #
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Streak
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Hours
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {ranking.length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-4 py-4 text-center text-sm text-gray-500"
                            >
                              No ranking data available yet.
                            </td>
                          </tr>
                        ) : (
                          ranking.map((entry, index) => (
                            <tr key={`${entry.name}-${index}`}>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                {index + 1}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                {entry.name}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                {entry.streak}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                {entry.total_hours}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tips */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              💡 Productivity Tips
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Keep your streak going by using Pomozid daily</li>
              <li>
                • Pomodoro counts reset daily to encourage consistent practice
              </li>
              <li>• Task completion rate shows your planning accuracy</li>
              <li>
                • Try to estimate pomodoros accurately to improve your planning
                skills
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
